/**
 * §P.2 · Quién da un grupo.
 *
 * Dos reglas del servidor, y las dos se respetan **antes** de enviar para no
 * ofrecer una combinación que se sabe que va a fallar:
 *
 * **Un solo principal** (`422 two_head_coaches`). «Es quien responde por el grupo,
 * y dos personas respondiendo significa que no responde ninguna.» Así que marcar a
 * un segundo **degrada al primero** en la propia pantalla, como el contacto
 * principal de un tutor.
 *
 * **Solo miembros activos del club** (`422 not_a_club_member`). La clave foránea
 * apunta a `users`, así que la base aceptaría a cualquiera con cuenta en Kiap
 * —incluida una madre de otro club—: la comprobación está en la aplicación porque
 * es la diferencia entre asignar a un compañero y dar acceso a un desconocido. Por
 * eso aquí no hay un campo de correo libre: se elige de la lista del personal.
 */

export type PapelDeProfesor = 'principal' | 'ayudante';

export interface Profesor {
  readonly user_id: string;
  readonly name: string | null;
  readonly email: string | null;
  readonly role: string;
}

/** Alguien del personal del club, candidato a dar un grupo. */
export interface DelPersonal {
  readonly user_id: string;
  readonly name: string | null;
  readonly email: string | null;
  readonly role: string;
  readonly status: string;
}

export const MAXIMO_DE_PROFESORES = 10;

/** Lo que se manda en el `PUT`. */
export interface Asignacion {
  readonly user_id: string;
  readonly role: PapelDeProfesor;
}

/**
 * Los que pueden dar clase: **miembros activos** y de los roles que dan clase.
 *
 * Un administrativo que no pisa el tatami no es candidato; el dueño sí, porque en
 * un club pequeño da clase a diario. Y un `invited` no: todavía no ha entrado.
 */
export const puedenDarClase = (
  personal: readonly DelPersonal[]
): readonly DelPersonal[] =>
  personal.filter(
    (p) =>
      p.status === 'active' &&
      (p.role === 'owner' || p.role === 'admin' || p.role === 'coach')
  );

/**
 * Marca a alguien como principal y **degrada al anterior**.
 *
 * Es la misma decisión que «contacto principal» en los tutores: marcarlo no falla
 * si ya hay otro, hace lo que el club quiere decir al pulsarlo.
 */
export function hacerPrincipal(
  asignados: readonly Asignacion[],
  userId: string
): readonly Asignacion[] {
  return asignados.map((a) => ({
    ...a,
    role: a.user_id === userId ? 'principal' : 'ayudante',
  }));
}

/** Cuántos principales hay. Debería ser siempre 0 o 1. */
export const cuantosPrincipales = (asignados: readonly Asignacion[]): number =>
  asignados.filter((a) => a.role === 'principal').length;

/**
 * Añade a alguien. El primero entra como principal; los demás, como ayudantes.
 *
 * Un grupo con un solo profesor y ese profesor marcado como ayudante sería un
 * grupo del que no responde nadie.
 */
export function anadir(
  asignados: readonly Asignacion[],
  userId: string
): readonly Asignacion[] {
  if (asignados.some((a) => a.user_id === userId)) return asignados;

  const papel: PapelDeProfesor =
    cuantosPrincipales(asignados) === 0 ? 'principal' : 'ayudante';

  return [...asignados, { user_id: userId, role: papel }];
}

/**
 * Quita a alguien, y si era el principal **asciende al primer ayudante**.
 *
 * Dejar un grupo con dos ayudantes y ningún principal es dejarlo sin nadie que
 * responda, y el servidor lo aceptaría: la regla es «no más de uno», no «al menos
 * uno». Esta es la parte que la pantalla tiene que poner de su parte.
 */
export function quitar(
  asignados: readonly Asignacion[],
  userId: string
): readonly Asignacion[] {
  const quedan = asignados.filter((a) => a.user_id !== userId);
  if (quedan.length === 0 || cuantosPrincipales(quedan) > 0) return quedan;

  return quedan.map((a, i) => (i === 0 ? { ...a, role: 'principal' } : a));
}
